import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { trackEvent } from "../lib/analytics";

const DISMISS_KEY = "bb_install_dismissed_at";
const DISMISS_COOLDOWN_DAYS = 14;

function recentlyDismissed() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const ms = Date.now() - parseInt(v, 10);
    return ms < DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function isStandalone() {
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    );
  } catch {
    return false;
  }
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent || "");
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    // Android/Chrome path — capture beforeinstallprompt
    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Delay 10s so we don't ambush visitors the moment they land
      setTimeout(() => setVisible(true), 10000);
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    // iOS path — no native prompt available; show a manual hint after browsing
    if (isIOS()) {
      const t = setTimeout(() => { setIosHint(true); setVisible(true); }, 20000);
      return () => {
        window.removeEventListener("beforeinstallprompt", onBefore);
        clearTimeout(t);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (err) { console.debug("localStorage unavailable:", err?.name); }
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    trackEvent("install_prompt_accepted", { source: "custom" });
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      trackEvent("install_prompt_result", { outcome: choice?.outcome || "unknown" });
    } catch (err) {
      console.error("Install prompt failed:", err);
    }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-24 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm bg-white border border-[#E5E0D8] rounded-2xl shadow-lg p-4 flex items-start gap-3 z-40"
      data-testid="pwa-install-prompt"
    >
      <div className="w-10 h-10 rounded-xl bg-[#2D4A3E] text-[#F9F6F0] flex items-center justify-center flex-shrink-0">
        <Download className="w-5 h-5" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-serif text-lg leading-snug mb-1">Install Backyard Buddy</p>
        {iosHint ? (
          <p className="text-sm text-[#5C6B62] font-light leading-snug">
            Tap <span className="font-medium">Share</span> in Safari, then{" "}
            <span className="font-medium">Add to Home Screen</span> for a one-tap app icon.
          </p>
        ) : (
          <p className="text-sm text-[#5C6B62] font-light leading-snug">
            Add to your home screen for one-tap access to your garden.
          </p>
        )}
        {!iosHint && (
          <button
            onClick={install}
            className="btn-primary mt-3 text-sm px-3 py-1.5"
            data-testid="pwa-install-accept"
          >
            Install
          </button>
        )}
      </div>
      <button
        onClick={dismiss}
        className="text-[#5C6B62] hover:text-[#1A2F25] p-1 -mr-1 -mt-1"
        aria-label="Dismiss"
        data-testid="pwa-install-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
